import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { DonateButton } from "@/components/site/DonateButton";
import { ASSETS, PRIMARY_NAV, MORE_NAV, ALL_NAV } from "@/data/site";
import { cn } from "@/lib/utils";

const Wordmark = () => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
    <img
      src={ASSETS.logo}
      alt="RDA Glasgow logo — a horse and heart in teal and brown"
      className="h-11 w-11 rounded-xl object-cover shadow-soft"
    />
    <span className="flex flex-col leading-none">
      <span className="font-heading font-extrabold text-lg text-rda-ink tracking-tight">
        RDA Glasgow
      </span>
      <span className="hidden sm:block text-[11px] text-rda-muted mt-0.5">
        Riding for the Disabled Association
      </span>
    </span>
  </Link>
);

const linkClass = ({ isActive }) =>
  cn(
    "text-sm font-medium transition-colors hover:text-rda-teal",
    isActive ? "text-rda-teal" : "text-rda-ink"
  );

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        scrolled
          ? "bg-rda-offwhite/85 backdrop-blur-md border-b border-border shadow-soft"
          : "bg-rda-offwhite/60 backdrop-blur-sm border-b border-transparent"
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Wordmark />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {PRIMARY_NAV.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass} data-testid={`nav-${item.to.slice(1)}`}>
                {item.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1 text-sm font-medium text-rda-ink hover:text-rda-teal transition-colors outline-none"
                data-testid="nav-more-trigger"
              >
                More <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                {MORE_NAV.map((item) => (
                  <DropdownMenuItem key={item.to} asChild>
                    <Link to={item.to} data-testid={`nav-more-${item.to.slice(1)}`}>
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              asChild
              variant="outline"
              className="rounded-full border-rda-teal text-rda-teal hover:bg-rda-teal hover:text-white font-semibold"
              data-testid="header-volunteer-cta"
            >
              <Link to="/volunteering">Volunteer</Link>
            </Button>
            <DonateButton />
          </div>

          {/* Mobile hamburger */}
          <div className="flex md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger" aria-label="Open menu">
                  <Menu className="h-6 w-6 text-rda-ink" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-rda-offwhite">
                <SheetTitle className="sr-only">Site navigation</SheetTitle>
                <div className="mb-6">
                  <Wordmark />
                </div>
                <nav className="flex flex-col gap-1" data-testid="mobile-nav">
                  {ALL_NAV.map((item) => (
                    <SheetClose asChild key={item.to}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          cn(
                            "rounded-xl px-4 py-3 text-base font-medium transition-colors",
                            isActive
                              ? "bg-rda-teal/10 text-rda-teal"
                              : "text-rda-ink hover:bg-rda-teal/5"
                          )
                        }
                        data-testid={`mobile-nav-${item.to.slice(1)}`}
                      >
                        {item.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-6 flex flex-col gap-3">
                  <SheetClose asChild>
                    <Button
                      asChild
                      variant="outline"
                      className="rounded-full border-rda-teal text-rda-teal font-semibold w-full"
                    >
                      <Link to="/volunteering">Volunteer</Link>
                    </Button>
                  </SheetClose>
                  <DonateButton className="w-full" />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
